'use client';

import {useSearchParams} from 'next/navigation';

import type {ProductAdapter} from '../adapter';
import type {ProductConfigTypeOf} from '../configuration';
import {getPriceForConfiguration, productConfigurationFromSearchParams} from '../configuration';
import type {UnionProductFragment} from '../query';
import {PRODUCT_PAGE_ALL_SLUG} from '../schema/constants';

// FIXME: The F type is currently limited to StickerProductFragment because it's the only product type
//        that uses this hook right now. It used to be the more general UnionProductFragment type
//        but that no longer works after merging in the cab interior branch. Those types don't
//        have a variants field.
//
//        I'm sure we can work around that somehow (I don't think we need to treat the selected
//        variant differently from any other configuration), but for now I just need the current use
//        case for this function to work.
export type UseProductPageArgs<F extends UnionProductFragment, V extends {isValid: boolean}> = {
	fragment: F;
	validate: (product: F, configuration: ProductConfigTypeOf<F>) => V;
	adapter: ProductAdapter<F>;
};

export function useProductPage<F extends UnionProductFragment, V extends {isValid: boolean}>({
	fragment,
	validate,
	adapter,
}: UseProductPageArgs<F, V>) {
	const searchParams = useSearchParams();

	const configuration =
		productConfigurationFromSearchParams(fragment, searchParams) ??
		adapter.getDefaultConfig(fragment);

	const validation = validate(fragment, configuration);
	const isValid = validation.isValid;
	const selectedItems = adapter.getItems(fragment, configuration);
	const priceIncVat = getPriceForConfiguration(fragment, configuration).priceIncVat;

	const category = fragment.category;
	const subcategory = fragment.subcategory;

	const slug = `${subcategory?.fullSlug}/${fragment.slug?.current}`;

	const breadcrumbs = [
		{
			href: `/${PRODUCT_PAGE_ALL_SLUG}`,
			title: 'Produkter',
		},
		{
			href: `/${PRODUCT_PAGE_ALL_SLUG}/${category?.slug}`,
			title: category?.name ?? '',
		},
		{
			href: `/${PRODUCT_PAGE_ALL_SLUG}/${subcategory?.fullSlug}`,
			title: subcategory?.name ?? '',
		},
		{
			href: `/${PRODUCT_PAGE_ALL_SLUG}/${fragment.fullSlug}`,
			title: fragment.name ?? '',
			active: true,
		},
	];

	return {
		slug,
		isValid,
		configuration,
		validation,
		priceIncVat,
		selectedItems,
		breadcrumbs,
	};
}
